import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useGlobalToast } from "../../../../stopOldToastGlobally";
import CustomPagination from "../../../../components/CustomPagination";
import { useDispatch, useSelector } from "react-redux";
import { commonSaveData, initSaveState } from "../../../../redux/slices/commonSlice";
import { calculateEarnedSalaryPath, payrollProcessPath } from "../../../../services/config";
import { config } from "../../../../services/configHeader";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import jtsLogo from "../../../../assets/Images/jts_logo.jpg";
const baseUrl = process.env.REACT_APP_BASE_URI;

const formatInr = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(String(n).replace(/,/g, ""));
  if (Number.isNaN(num)) return "—";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const mapStaffDetailToRow = (s) => {
  const emp = s.empStaffDetails || {};
  const sc = s.salaryCountDays || {};
  const basic = s.basicPay ?? emp.basicPay ?? sc.basicPay ?? 0;
  const allowances =
    s.totalAllowance ?? s.allowancesTotal ?? emp.totalAllowance ?? emp.allowances ?? 0;
  const gross = s.CostToCompany ?? s.grossSalary ?? emp.grossSalary ?? emp.costToCompany ?? 0;
  const dedRaw = s.totalDeduction ?? s.deductions ?? emp.totalDeduction ?? 0;
  const net = s.netSalary ?? emp.netSalary ?? (Number(gross) - Math.abs(Number(dedRaw)));
  const fin = sc.finalizePayrollStaff ?? s.finalizePayrollStaff;
  let status = "Ready";
  let statusType = "success";
  if (fin === "1" || fin === 1) {
    status = "Finalized";
    statusType = "accent";
  } else if (s.payrollStatus === "Review" || s.status === "Review") {
    status = "Review";
    statusType = "warning";
  } else if (s.payrollStatus === "Hold" || s.status === "Hold") {
    status = "Hold";
    statusType = "error";
  }
  const name = s.staffName || "—";
  const avatar =
    s.avatar ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=8660F8&color=fff`;
  return {
    key: String(s.staffId ?? s.employeeId ?? name),
    staffId: s.staffId,
    name,
    role: s.jobTitle || emp.jobTitle || "—",
    id: s.employeeId || "—",
    dept: s.department || emp.department || "—",
    basic: formatInr(basic),
    allowances: formatInr(allowances),
    gross: formatInr(gross),
    deductions: formatInr(-Math.abs(Number(dedRaw) || 0)),
    net: formatInr(net),
    status,
    statusType,
    avatar,
  };
};

const RunPayroll = () => {
  const customToast = useGlobalToast();
  const dispatch = useDispatch();
  const commonStore = useSelector((s) => s.common);
  const branchList = useSelector((s) => s.branch?.brnGetData || []);
  const departmentList = useSelector((s) => s.department?.depGetData || []);

  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user_data") || "{}"),
    []
  );

  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedType, setSelectedType] = useState("All Types");
  const [selectedPayPeriod, setSelectedPayPeriod] = useState("Monthly");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showPostRun, setShowPostRun] = useState(false);
  const [runPayrollPage, setRunPayrollPage] = useState(0);
  const [runPayrollPageSize, setRunPayrollPageSize] = useState(10);
  const [employeeRows, setEmployeeRows] = useState([]);
  const [salarySummary, setSalarySummary] = useState(null);
  const [employeeRowsRaw, setEmployeeRowsRaw] = useState([]);
  const [preRunChecklist, setPreRunChecklist] = useState([]);
  const [preRunReady, setPreRunReady] = useState(false);
  const [preRunLoading, setPreRunLoading] = useState(false);
  const [preRunError, setPreRunError] = useState("");
  const [runExceptions, setRunExceptions] = useState(null);
  const monthLabel = useMemo(() => {
    const [y, m] = selectedMonth.split("-");
    if (!y || !m) return "";
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleString("default", { month: "long", year: "numeric" });
  }, [selectedMonth]);

  const loadPreRunChecks = useCallback(async () => {
    if (!user.user_id) return;

    setPreRunLoading(true);
    setPreRunError("");

    const requestPayload = {
      action: "fetchPayrollReadiness",
      userId: user.user_id,
      selectedMonth,
      branchId: selectedBranch || "",
      departmentId: selectedDepartment || "",
      shiftId: "",
    };

    try {
      const [readinessResponse, exceptionsResponse] = await Promise.all([
        axios.post(`${baseUrl}${payrollProcessPath}`, requestPayload, config),
        axios.post(
          `${baseUrl}${payrollProcessPath}`,
          {
            ...requestPayload,
            action: "fetchRunPayrollExceptions",
          },
          config
        ),
      ]);

      const readinessData = readinessResponse?.data?.data || {};
      setPreRunChecklist(Array.isArray(readinessData.items) ? readinessData.items : []);
      setPreRunReady(Boolean(readinessData.ready));
      setRunExceptions(exceptionsResponse?.data?.data || null);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Unable to load pre-run checklist";
      setPreRunError(message);
      setPreRunChecklist([]);
      setPreRunReady(false);
      setRunExceptions(null);
    } finally {
      setPreRunLoading(false);
    }
  }, [user.user_id, selectedMonth, selectedBranch, selectedDepartment]);

  const loadEarnedSalaryList = useCallback(() => {
    if (!user.user_id) return;
    dispatch(
      commonSaveData({
        path: calculateEarnedSalaryPath,
        data: {
          userId: user.user_id,
          selectedDate: `${selectedMonth}-01`,
          branchId: selectedBranch || null,
          departmentId: selectedDepartment || null,
          shiftId: null,
        },
      })
    );
  }, [dispatch, user.user_id, selectedMonth, selectedBranch, selectedDepartment]);

  useEffect(() => {
    loadEarnedSalaryList();
  }, [loadEarnedSalaryList]);

  useEffect(() => {
    loadPreRunChecks();
  }, [loadPreRunChecks]);

  useEffect(() => {
          if (commonStore.commonSuccess) {
            const payload = commonStore.commonData?.staffDetails
        ? commonStore.commonData
        : commonStore.commonData?.data;   // ← unwrap the extra .data layer

      if (payload?.staffDetails) {
        setEmployeeRows((payload.staffDetails || []).map(mapStaffDetailToRow));
        setSalarySummary(payload.summary || null);
        setEmployeeRowsRaw(payload.staffDetails || []);
      }else {
        setEmployeeRows([]);
        setSalarySummary(commonStore.commonData?.summary || null);
      }
      dispatch(initSaveState());
    }
    if (commonStore.commonError) {
      customToast.error(commonStore.commonMessage || "Request failed");
      dispatch(initSaveState());
    }
  }, [
    commonStore.commonSuccess,
    commonStore.commonError,
    commonStore.commonData,
    commonStore.commonMessage,
    dispatch,
    customToast,
  ]);

  const kpiData = useMemo(() => {
    const s = salarySummary || {};
    const count = employeeRows.length;
    const occ = s.OverallCostToCompany ?? s.overallCostToCompany;
    const gross = s.TotalGross ?? s.totalGross ?? s.totalGrossSalary ?? s.OverAllGrossSalary;
    const net = s.TotalNet ?? s.totalNet ?? s.totalNetSalary ?? s.OverAllNetSalary;
    return [
      {
        icon: "fa-solid fa-wallet",
        iconColor: "var(--primary)",
        glow: "rgba(134,96,248,0.12)",
        label: "Total payroll (CTC)",
        badge: "",
        bc: "status-chip-accent",
        value: occ != null && occ !== "" ? formatInr(occ) : "—",
        sub: "From calculateEarnedSalary summary",
      },
      {
        icon: "fa-solid fa-money-bill-trend-up",
        iconColor: "var(--color-success)",
        glow: "rgba(74,222,128,0.10)",
        label: "Total gross",
        badge: "",
        bc: "status-chip-success",
        value: gross != null && gross !== "" ? formatInr(gross) : "—",
        sub: "Before deductions (if provided)",
      },
      {
        icon: "fa-solid fa-hand-holding-dollar",
        iconColor: "#22d3ee",
        glow: "rgba(34,211,238,0.10)",
        label: "Total net",
        badge: "",
        bc: "status-chip-success",
        value: net != null && net !== "" ? formatInr(net) : "—",
        sub: "After deductions (if provided)",
      },
      {
        icon: "fa-solid fa-users",
        iconColor: "var(--color-warning)",
        glow: "rgba(245,158,11,0.10)",
        label: "Employees",
        badge: "",
        bc: "status-chip-success",
        value: count ? String(count) : "0",
        sub: "Loaded for selected period",
      },
      {
        icon: "fa-solid fa-building-columns",
        iconColor: "var(--color-success)",
        glow: "rgba(74,222,128,0.10)",
        label: "Period",
        badge: "",
        bc: "status-chip-accent",
        value: monthLabel || selectedMonth,
        sub: "Payroll month",
      },
      {
        icon: "fa-solid fa-triangle-exclamation",
        iconColor: "var(--color-danger)",
        glow: "rgba(239,68,68,0.10)",
        label: "Filters",
        badge: "",
        bc: "status-chip-error",
        value:
          [selectedBranch && "Branch", selectedDepartment && "Dept"].filter(Boolean).join("+") ||
          "All",
        sub: "Branch / department scope",
      },
    ];
  }, [salarySummary, employeeRows.length, monthLabel, selectedMonth, selectedBranch, selectedDepartment]);

  const filteredEmployeeRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return employeeRows;
    return employeeRows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        String(r.id).toLowerCase().includes(q) ||
        r.dept.toLowerCase().includes(q)
    );
  }, [employeeRows, searchTerm]);

  const pagedRows = useMemo(() => {
    const start = runPayrollPage * runPayrollPageSize;
    return filteredEmployeeRows.slice(start, start + runPayrollPageSize);
  }, [filteredEmployeeRows, runPayrollPage, runPayrollPageSize]);

  useEffect(() => {
    setRunPayrollPage(0);
    setSelectedEmployees([]);
  }, [selectedMonth, selectedBranch, selectedDepartment]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredEmployeeRows.length / runPayrollPageSize));
    const maxIdx = Math.max(0, totalPages - 1);
    if (runPayrollPage > maxIdx) setRunPayrollPage(maxIdx);
  }, [filteredEmployeeRows.length, runPayrollPageSize, runPayrollPage]);

  // ── Calculate total disbursement for selected employees only ──
  const selectedEmployeesTotal = useMemo(() => {
    if (selectedEmployees.length === 0) {
      return salarySummary?.OverallCostToCompany || 0;
    }
    const selectedRows = employeeRows.filter((emp) => selectedEmployees.includes(emp.key));
    return selectedRows.reduce((sum, emp) => {
      // Extract numeric value from formatted gross salary
      const grossStr = emp.gross;
      const grossNum = typeof grossStr === "string"
        ? Number(grossStr.replace(/[₹,]/g, "")) || 0
        : Number(grossStr) || 0;
      return sum + grossNum;
    }, 0);
  }, [selectedEmployees, employeeRows, salarySummary?.OverallCostToCompany]);

  const insightData = useMemo(() => {
    const s = salarySummary || {};
    return [
      {
        icon: "fa-solid fa-users",
        grad: "linear-gradient(135deg, var(--primary), #7c3aed)",
        shadow: "rgba(134,96,248,0.2)",
        label: "Queue size",
        value: String(filteredEmployeeRows.length),
        trend: "Employees matching filters",
        trendColor: "var(--color-success)",
      },
      {
        icon: "fa-solid fa-wallet",
        grad: "linear-gradient(135deg, var(--color-success), #16a34a)",
        shadow: "rgba(74,222,128,0.2)",
        label: "Period cost",
        value:
          s.OverallCostToCompany != null && s.OverallCostToCompany !== ""
            ? formatInr(s.OverallCostToCompany)
            : "—",
        trend: "Overall cost to company",
        trendColor: "var(--text-muted)",
      },
    ];
  }, [salarySummary, filteredEmployeeRows.length]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEmployees(filteredEmployeeRows.map((emp) => emp.key));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleEmployeeSelect = (empKey) => {
    if (selectedEmployees.includes(empKey)) {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== empKey));
    } else {
      setSelectedEmployees([...selectedEmployees, empKey]);
    }
  };

  const handleRunPayroll = () => {
    setShowPostRun(true);
    setTimeout(() => {
      document.getElementById("post-run-actions")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

const handleDownloadSummary = () => {
  const headers = [
    "S.No", "Full Name", "Employee Id", "Job Title", "Mobile Number", "Branch Names",
    "Other Allowances", "Other Allowances Yearly",
    "Basic Pay", "Basic Pay Yearly",
    "Hra Pay", "Hra Pay Yearly",
    "Employee Pf", "Employee Pf Yearly",
    "Employee Esi", "Employee Esi Yearly",
    "Professional Tax", "Professional Tax Yearly",
    "Employer Pf", "Employer Pf Yearly",
    "Employer Esi", "Employer Esi Yearly",
    "Employer Edli", "Employer Edli Yearly",
    "Admin Charges", "Admin Charges Yearly",
    "Gross Salary", "Gross Salary Yearly",
    "Net Salary", "Net Salary Yearly",
    "Lwf Amount", "Lwf Amount Yearly",
    "Employer Lwf Amount", "Employer Lwf Amount Yearly",
    "Employee PFText",
    "Total Employee Contributions", "Total Employee Contributions Yearly",
    "Total Employer Contributions", "Total Employer Contributions Yearly",
    "Cost To Company", "Cost To Company Yearly",
  ];

  const dataRows = employeeRows.map((r, idx) => {
    const raw = employeeRowsRaw[idx] || {};
    const clean = (v) => Number(String(v || "0").replace(/[₹,]/g, "")) || 0;
    const yearly = (v) => (clean(v) * 12).toFixed(2);

    const basicMonthly  = clean(r.basic);
    const hraMonthly    = raw.hraPay || 0;
    const otherMonthly  = raw.otherAllowance || 0;
    const grossMonthly  = raw.grossSalary || 0;
    const netMonthly    = raw.netSalary || 0;
    const empPF         = raw.totalEmployeePF || 0;
    const empESI        = raw.totalEmployeeESI || 0;
    const empLWF        = raw.totalEmployeeLWF || 0;
    const profTax       = raw.totalProfessionalTax || 0;
    const emplrPF       = raw.totalEmployerPF || 0;
    const emplrESI      = raw.totalEmployerESI || 0;
    const edli          = raw.totalEDLICharges || 0;
    const adminCharges  = raw.adminCharges || 0;
    const emplrLWF      = raw.totalEmployerLWF || 0;
    const ctc           = raw.CostToCompany || 0;

    const totalEmpContrib   = clean(empPF) + clean(empESI) + clean(empLWF) + clean(profTax);
    const totalEmplrContrib = clean(emplrPF) + clean(emplrESI) + clean(edli) + clean(adminCharges) + clean(emplrLWF);

    return [
      idx + 1,
      r.name,
      r.id,
      r.role,
      raw.mobileNumber || "-",
      raw.branchNames || "-",
      otherMonthly,        yearly(otherMonthly),
      basicMonthly,        yearly(basicMonthly),
      hraMonthly,          yearly(hraMonthly),
      empPF,               yearly(empPF),
      empESI,              yearly(empESI),
      profTax,             yearly(profTax),
      emplrPF,             yearly(emplrPF),
      emplrESI,            yearly(emplrESI),
      edli,                yearly(edli),
      adminCharges,        yearly(adminCharges),
      grossMonthly,        yearly(grossMonthly),
      netMonthly,          yearly(netMonthly),
      empLWF,              yearly(empLWF),
      emplrLWF,            yearly(emplrLWF),
      raw.employeePFText || "-",
      totalEmpContrib.toFixed(2),          (totalEmpContrib * 12).toFixed(2),
      totalEmplrContrib.toFixed(2),        (totalEmplrContrib * 12).toFixed(2),
      ctc,                 yearly(ctc),
    ];
  });

  const csvContent = [headers, ...dataRows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${selectedMonth}-01_Salary_Detail.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
const handleSendPayslips = async () => {
  if (employeeRowsRaw.length === 0) {
    customToast.error("No employee data to generate payslips.");
    return;
  }

  customToast.success("Generating payslips, please wait...");

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = 210;
  const pageHeight = 297;

  for (let i = 0; i < employeeRowsRaw.length; i++) {
    const raw = employeeRowsRaw[i];
    const sc = raw.salaryCountDays || {};
    const da = (raw.allowanceList || []).find(a => a.name === "Dearness Allowance")?.monthlyAmount || "0.00";
    const insurance = (raw.newAllowanceList || []).find(a => a.name === "Insurance")?.monthlyAmount || "0.00";
    const specialAllowance = (raw.allowanceList || []).find(a => a.name === "Special Allowance")?.monthlyAmount || "0.00";

    // Create a hidden div, render it, capture with html2canvas
    const div = document.createElement("div");
    div.style.cssText = "position:fixed;left:-9999px;top:0;width:720px;background:#fff;font-family:Arial,sans-serif;font-size:11px;";
    div.innerHTML = `
  <div style="border:1px solid #999;width:720px;background:#fff;color:#000;font-family:Arial,sans-serif;font-size:11px;">
    
    <!-- Header -->
    <div style="display:flex;align-items:center;padding:14px 20px;border-bottom:2px solid #333;background:#fff;">
      <div style="width:64px;height:64px;background:#1a237e;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:bold;font-size:20px;margin-right:16px;flex-shrink:0;border:3px solid #1a237e;">JTS</div>
      <div>
        <div style="font-size:20px;font-weight:bold;color:#1a237e;margin-bottom:2px;">JTS Business Services</div>
        <div style="font-size:11px;color:#333;margin-bottom:2px;">(OPC) Private Limited</div>
        <div style="font-size:10px;color:#555;">No. 3, 2nd Floor, JayPee Towers, East End A Main Rd, Jayanagar 9th block, Bangalore - 69</div>
      </div>
    </div>

    <!-- Employee Info -->
    <table style="width:100%;border-collapse:collapse;font-size:11px;color:#000;">
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;width:18%;background:#f0f0f0;font-weight:bold;color:#000;">Employee ID</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:32%;background:#fff;color:#000;">${raw.employeeId || "-"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:18%;background:#f0f0f0;font-weight:bold;color:#000;">Month</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:32%;background:#fff;color:#000;">${monthLabel || selectedMonth}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Employee Name</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.staffName || "-"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">UAN</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.uanNumber || "-"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Date of Joining</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.dateOfJoin || "-"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">PAN Number</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">-</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Designation</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.jobTitle || "-"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Total No of Days</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${sc.totalDaysInMonth || "-"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Department</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.department || "-"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">No of Days Worked</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${sc.presentDaysCount || 0}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Deputed At</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">-</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">LOP</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${sc.absentDaysCount || 0}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Location</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">-</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Bank Account No</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.accountNumber || "-"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Leave Balance</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">-</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#f0f0f0;font-weight:bold;color:#000;">Utilized Leave</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${sc.leaveDaysCount || 0}</td>
      </tr>
    </table>

    <!-- Earnings & Deductions Header -->
    <table style="width:100%;border-collapse:collapse;font-size:11px;color:#000;">
      <tr>
        <td colspan="2" style="padding:6px 8px;border:1px solid #ccc;background:#dde1f0;font-weight:bold;text-align:center;color:#000;">Earnings</td>
        <td colspan="2" style="padding:6px 8px;border:1px solid #ccc;background:#dde1f0;font-weight:bold;text-align:center;color:#000;">Deductions</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;color:#000;">Basic</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;color:#000;">${raw.basicPay || "0.00"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;color:#000;">Provident Fund</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;color:#000;">${raw.totalEmployeePF || "0.00"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">DRA</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${da}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">ESI</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.totalEmployeeESI || "0.00"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">HRA</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.hraPay || "0.00"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Professional Tax</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.totalProfessionalTax || "0.00"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Special Allowance</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${specialAllowance}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Insurance</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${insurance}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Incentives</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${sc.incentiveAmount || "0.00"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Allowance</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">${raw.otherAllowance || "0.00"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Statutory Bonus</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">0.00</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Variable Pay</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">0.00</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Attendance Bonus</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">0.00</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Conveyance</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">0.00</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">Medical Allowance</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;">0.00</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
      </tr>
    </table>

    <!-- Totals -->
    <table style="width:100%;border-collapse:collapse;font-size:11px;color:#000;margin-top:4px;">
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;font-weight:bold;color:#000;">Total Earnings</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;font-weight:bold;color:#000;">${raw.grossSalary || "0.00"}</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;font-weight:bold;color:#000;">Total Deduction</td>
        <td style="padding:5px 8px;border:1px solid #ccc;width:25%;background:#fff;font-weight:bold;color:#000;">${raw.totalEmployeeContributions || "0.00"}</td>
      </tr>
      <tr>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;color:#000;"></td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;font-weight:bold;color:#000;">Net Pay</td>
        <td style="padding:5px 8px;border:1px solid #ccc;background:#fff;font-weight:bold;color:#000;">${raw.netSalary || "0.00"}</td>
      </tr>
    </table>

    <!-- Footer -->
    <div style="padding:8px;text-align:center;border-top:1px solid #ccc;font-size:10px;color:#333;font-weight:bold;background:#fff;">
      This is a System generated payslip hence, it does not require a signature
    </div>
  </div>
`;

    document.body.appendChild(div);

    const canvas = await html2canvas(div, { 
      scale: 2, 
      useCORS: true, 
      backgroundColor: "#ffffff",
      logging: false
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, Math.min(imgHeight, pageHeight));

    document.body.removeChild(div);
  }

  pdf.save(`Payslips_${selectedMonth}.pdf`);
  customToast.success("Payslips downloaded successfully!");
};
  return (
    <div style={{ backgroundColor: "transparent", minHeight: "100vh", fontFamily: "var(--font)" }}>
      {/* Back Navigation */}
      <div
        onClick={() => window.location.href = "/payroll"}
        style={{
          textDecoration: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px",
          padding: "0 20px"
        }}
        className="color-white"
      >
        {/* <ArrowBack style={{ cursor: "pointer", fontSize: "20px" }} className="color-white" />
        <span style={{ fontSize: "14px" }}>Back to Payroll</span> */}
      </div>

      <main style={{ maxWidth: 1600, margin: "0 auto", padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 32 }}>

        {/* Header Section */}
        <header style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>Run Payroll</h1>
              <span style={{
                padding: "4px 12px",
                borderRadius: "9999px",
                background: "rgba(134,96,248,0.2)",
                color: "var(--primary)",
                fontSize: 10,
                fontWeight: 500,
                border: "1px solid rgba(134,96,248,0.3)"
              }}>
                {monthLabel || selectedMonth}
              </span>
            </div>
            <p style={{ fontSize: 14, margin: 0, color: "var(--text-muted)" }}>
              {commonStore.commonLoading
                ? "Loading payroll data…"
                : `Process salary for ${filteredEmployeeRows.length} employee${filteredEmployeeRows.length === 1 ? "" : "s"} in the queue (after search filters). Open a row to verify pay and finalize on the detailed run payroll screen.`}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button className="btn-ghost" style={{ padding: "10px 20px" }}>
              <i className="fa-solid fa-file-import" style={{ marginRight: 8 }} /> Import Adjustments
            </button>
            <button className="btn-secondary" style={{ padding: "10px 20px" }}>
              <i className="fa-solid fa-eye" style={{ marginRight: 8 }} /> Preview Payroll
            </button>
            <button
              className="btn-primary"
              style={{
                padding: "12px 28px",
                animation: "pulse-glow 2s ease-in-out infinite",
                cursor: "pointer"
              }}
              onClick={handleRunPayroll}
            >
              <i className="fa-solid fa-play" style={{ marginRight: 8 }} /> Run Payroll
            </button>
          </div>
        </header>

        {/* Filter Bar */}
        <div style={{
          background: "var(--pay-surface)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 14
        }}>
          {/* Month */}
          <div className="field-wrap field-month" style={{ flex: "1 1 160px" }}>
            <i className="field-icon fa-regular fa-calendar" />
            <input
              type="month"
              className="universal-input"
              style={{ width: "100%", paddingLeft: "30px", cursor: "pointer" }}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
          {/* Branch */}
          <div className="field-wrap field-branch has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-solid fa-building" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
              <option value="">All Branches</option>
              {branchList.map(b => <option key={b.branchId} value={b.branchId}>{b.branch_name}</option>)}
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Department */}
          <div className="field-wrap field-dept has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-solid fa-users" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
              <option value="">All Departments</option>
              {departmentList.map(d => <option key={d.departmentId} value={d.departmentId}>{d.department_name}</option>)}
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Type */}
          <div className="field-wrap field-type has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-solid fa-list-ul" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
              <option>All Types</option>
              <option>Full-Time</option>
              <option>Contractor</option>
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Pay Period */}
          <div className="field-wrap field-period has-right-icon" style={{ flex: "1 1 150px" }}>
            <i className="field-icon fa-regular fa-clock" />
            <select className="universal-input" style={{ width: "100%" }} value={selectedPayPeriod} onChange={(e) => setSelectedPayPeriod(e.target.value)}>
              <option>Monthly</option>
              <option>Weekly</option>
              <option>Bi-weekly</option>
            </select>
            <i className="field-icon-right fa-solid fa-chevron-down" />
          </div>
          {/* Search */}
          <div className="field-wrap field-search" style={{ flex: "2 1 250px" }}>
            <i className="field-icon fa-solid fa-magnifying-glass" />
            <input
              type="text"
              placeholder="Search by Employee ID or Name"
              className="universal-input"
              style={{ width: "100%" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* KPI Summary Cards */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 18 }}>
          {kpiData.map((k, i) => (
            <div
              key={i}
              className="glass-card runpay-kpi-card"
              style={{
                padding: 22,
                position: "relative",
                overflow: "hidden",
                transition: "all 0.25s ease-in-out",
                cursor: "pointer",
                transform: "translateY(0)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.15)";
                e.currentTarget.style.borderColor = "var(--primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.borderColor = "";
              }}
            >
              <div style={{
                position: "absolute",
                right: -20,
                top: -20,
                width: 90,
                height: 90,
                background: k.glow,
                borderRadius: "50%",
                filter: "blur(22px)",
                transition: "all 0.3s ease-in-out",
              }} />
              <div className="runpay-kpi-header">
                <div className="runpay-kpi-title">
                  <i className={k.icon} style={{ color: k.iconColor, fontSize: 14 }} />
                  {k.label}
                </div>
                {k.badge ? (
                  <span className={`${k.bc} runpay-kpi-badge`}>{k.badge}</span>
                ) : null}
              </div>
              <div className="runpay-kpi-value">{k.value}</div>
              <div className="runpay-kpi-sub">{k.sub}</div>
            </div>
          ))}
        </section>

        {/* Pre-Run Checklist */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>Pre-run checklist</h3>
              <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--text-muted)" }}>
                Complete attendance lock, approvals, and statutory validation in their respective modules before you finalize payroll for individuals.
              </p>
            </div>
            <button
              type="button"
              className="btn-ghost"
              style={{ fontSize: 12, padding: "6px 12px", border: "1px solid var(--border)" }}
              onClick={loadPreRunChecks}
              disabled={preRunLoading}
            >
              <i className="fa-solid fa-rotate" style={{ marginRight: 6 }} />
              {preRunLoading ? "Checking..." : "Refresh"}
            </button>
          </div>
          {preRunError ? (
            <div style={{ padding: 12, borderRadius: 12, background: "rgba(239,68,68,0.10)", color: "var(--color-danger)", fontSize: 12 }}>
              {preRunError}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 12 }}>
              {(preRunChecklist.length ? preRunChecklist : [
                { key: "loading", label: "Payroll readiness", count: 0, ok: false, message: preRunLoading ? "Checking payroll readiness..." : "No checklist data loaded." },
              ]).map((item) => (
                <div
                  key={item.key}
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    border: `1px solid ${item.ok ? "rgba(74,222,128,0.35)" : "rgba(245,158,11,0.35)"}`,
                    background: item.ok ? "rgba(74,222,128,0.08)" : "rgba(245,158,11,0.08)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{item.label}</div>
                    <span
                      className={item.ok ? "status-chip-success" : "status-chip-warning"}
                      style={{ fontSize: 10, padding: "3px 8px", whiteSpace: "nowrap" }}
                    >
                      {item.ok ? "OK" : item.count}
                    </span>
                  </div>
                  <p style={{ fontSize: 11, margin: "8px 0 0", color: "var(--text-muted)" }}>{item.message}</p>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 14, fontSize: 12, color: preRunReady ? "var(--color-success)" : "var(--color-warning)" }}>
            <i className={`fa-solid ${preRunReady ? "fa-circle-check" : "fa-triangle-exclamation"}`} style={{ marginRight: 6 }} />
            {preRunReady ? "Payroll is ready for the selected period." : "Resolve checklist items before final payroll run."}
          </div>
        </div>

        <div className="glass-card" style={{ padding: 24, borderLeft: "4px solid var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>Exceptions</h3>
              <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--text-muted)" }}>
                Review employees marked Review or Hold in the queue. Detailed overtime, fines, and adjustments are managed from the per-employee run payroll screen (/run-payroll).
              </p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
            {[
              ["Review / hold", runExceptions?.summary?.reviewHoldEmployees || 0],
              ["Missing punch > 2 days", runExceptions?.summary?.missingPunch || 0],
              ["Excess OT > 10 hrs", runExceptions?.summary?.excessOT || 0],
              ["Unapproved absences", runExceptions?.summary?.unapprovedAbsences || 0],
            ].map(([label, count]) => (
              <div key={label} style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 12 }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: Number(count) > 0 ? "var(--color-warning)" : "var(--color-success)" }}>
                  {preRunLoading ? "..." : count}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Employee Payroll Queue Table */}
        <div className="glass-card" style={{ overflow: "hidden" }}>
          <div style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16
          }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>Employee Payroll Queue</h3>
              <p style={{ fontSize: 12, margin: "4px 0 0", color: "var(--text-muted)" }}>Individual salary processing status and controls</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-ghost" style={{ fontSize: 13, padding: "6px 14px", border: "1px solid var(--border)" }}>
                <i className="fa-solid fa-check-double" style={{ marginRight: 8 }} /> Bulk Approve
              </button>
              <button className="btn-ghost" style={{ fontSize: 13, padding: "6px 14px", border: "1px solid var(--border)" }}>
                <i className="fa-solid fa-file-excel" style={{ marginRight: 8 }} /> Export Excel
              </button>
              <button className="btn-ghost" style={{ fontSize: 13, padding: "6px 14px", border: "1px solid var(--border)" }}>
                <i className="fa-solid fa-filter" style={{ marginRight: 8 }} /> Filter
              </button>
            </div>
          </div>

        <div style={{ overflowX: "auto" }}>
  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 960 }}>
    <thead>
      <tr style={{ background: "var(--pay-surface-2)" }}>
        <th style={{ padding: "10px 12px", width: 40 }}>
          <label style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <input type="checkbox" onChange={handleSelectAll} checked={selectedEmployees.length === filteredEmployeeRows.length && filteredEmployeeRows.length > 0} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
            <span style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 16, height: 16, borderRadius: 4,
              border: selectedEmployees.length === filteredEmployeeRows.length && filteredEmployeeRows.length > 0
                ? "2px solid #8660F8"
                : "2px solid rgba(134,96,248,0.4)",
              background: selectedEmployees.length === filteredEmployeeRows.length && filteredEmployeeRows.length > 0
                ? "#8660F8"
                : "rgba(134,96,248,0.06)",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}>
              {selectedEmployees.length === filteredEmployeeRows.length && filteredEmployeeRows.length > 0 && (
                <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                  <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {selectedEmployees.length > 0 && selectedEmployees.length < filteredEmployeeRows.length && (
                <svg width="8" height="2" viewBox="0 0 8 2" fill="none">
                  <rect x="0" y="0.5" width="8" height="1" rx="0.5" fill="#fff"/>
                </svg>
              )}
            </span>
          </label>
        </th>
        <th style={{ padding: "12px 14px", minWidth: 220, fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "left" }}>Employee</th>
        <th style={{ padding: "12px 14px", minWidth: 180, fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "left" }}>Employee ID / Department</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Basic</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Allowances</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Gross</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Deductions</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Net</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "left" }}>Status</th>
        <th style={{ padding: "12px 14px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", color: "var(--text-muted)", textAlign: "right" }}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {pagedRows.length === 0 ? (
        <tr>
          <td colSpan={10} style={{ padding: 24, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            {commonStore.commonLoading ? "Loading…" : "No employees in the queue for this period and filters."}
          </td>
        </tr>
      ) : (
        pagedRows.map((row) => (
        <tr key={row.key} className="table-row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
          <td style={{ padding: "12px 14px" }}>
            <label style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={selectedEmployees.includes(row.key)} onChange={() => handleEmployeeSelect(row.key)} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
              <span style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 16, height: 16, borderRadius: 4,
                border: selectedEmployees.includes(row.key)
                  ? "2px solid #8660F8"
                  : "2px solid rgba(134,96,248,0.35)",
                background: selectedEmployees.includes(row.key)
                  ? "#8660F8"
                  : "rgba(134,96,248,0.05)",
                transition: "all 0.15s ease",
                flexShrink: 0,
              }}>
                {selectedEmployees.includes(row.key) && (
                  <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                    <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
            </label>
          </td>
          <td style={{ padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src={row.avatar} alt={row.name} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", whiteSpace: "normal", wordBreak: "break-word" }}>{row.name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3, whiteSpace: "normal", wordBreak: "break-word" }}>{row.role}</div>
              </div>
            </div>
          </td>
          <td style={{ padding: "12px 14px" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>{row.id}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.dept}</div>
            </div>
          </td>
          <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 600, fontSize: 12 }}>{row.basic}</td>
          <td style={{ padding: "12px 14px", textAlign: "right", color: "var(--color-success)", fontSize: 12 }}>{row.allowances}</td>
          <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 700, fontSize: 12 }}>{row.gross}</td>
          <td style={{ padding: "12px 14px", textAlign: "right", color: "var(--color-danger)", fontSize: 12 }}>{row.deductions}</td>
          <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 700, fontSize: 12 }}>{row.net}</td>
          <td style={{ padding: "12px 14px", textAlign: "left" }}>
            <span className={`status-chip status-chip-${row.statusType}`} style={{ fontSize: 11, padding: "6px 10px", textTransform: "capitalize" }}>{row.status}</span>
           </td>
          <td style={{ padding: "12px 14px", textAlign: "right" }}>
            {row.staffId ? (
              <Link
                className="btn-ghost"
                style={{ padding: "0 6px", minWidth: 34, textDecoration: "none" }}
                to={`/run-payroll?user=${encodeURIComponent(user.user_id)}&staffId=${encodeURIComponent(row.staffId)}&staffName=${encodeURIComponent(row.name)}&month=${encodeURIComponent(selectedMonth)}`}
              >
                <i className="fa-solid fa-eye" style={{ fontSize: 12 }} title="Open run payroll" />
              </Link>
            ) : (
              <button type="button" className="btn-ghost" style={{ padding: "0 6px", minWidth: 34 }} disabled><i className="fa-solid fa-eye" style={{ fontSize: 12, opacity: 0.4 }} /></button>
            )}
            <button type="button" className="btn-ghost" style={{ padding: "0 6px", minWidth: 34 }} disabled title="Coming soon"><i className="fa-solid fa-pen-to-square" style={{ fontSize: 12, opacity: 0.4 }} /></button>
            <button type="button" className="btn-ghost" style={{ padding: "0 6px", minWidth: 34 }} disabled title="Coming soon"><i className="fa-solid fa-ellipsis-vertical" style={{ fontSize: 12, opacity: 0.4 }} /></button>
           </td>
         </tr>
        ))
      )}
    </tbody>
  </table>
</div>

          {/* Pagination */}
          <div style={{
            padding: "10px 16px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 11,
            color: "var(--text-muted)"
          }}>
            <span>
              {filteredEmployeeRows.length === 0
                ? "0 employees"
                : `Showing ${runPayrollPage * runPayrollPageSize + 1}–${Math.min((runPayrollPage + 1) * runPayrollPageSize, filteredEmployeeRows.length)} of ${filteredEmployeeRows.length} employees`}
            </span>
            <CustomPagination
              total={filteredEmployeeRows.length}
              page={runPayrollPage}
              pageSize={runPayrollPageSize}
              onPageChange={setRunPayrollPage}
              onPageSizeChange={(s) => { setRunPayrollPageSize(s); setRunPayrollPage(0); }}
            />
          </div>
        </div>

        {/* Smart Insights */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 16 }}>
          {insightData.map((item, idx) => (
            <div
              key={idx}
              className="glass-card"
              style={{
                padding: 20,
                display: "flex",
                alignItems: "flex-start",
                gap: 16,
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--primary)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: item.grad,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: `0 4px 12px ${item.shadow}`
              }}>
                <i className={item.icon} style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 4, color: "var(--text-muted)" }}>{item.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)" }}>{item.value}</div>
                <div style={{ fontSize: 11, marginTop: 4, color: item.trendColor, display: "flex", alignItems: "center", gap: 4 }}>{item.trend}</div>
              </div>
            </div>
          ))}
        </section>

        {/* Post Run Actions - Success Message */}
        <section
          id="post-run-actions"
          className="glass-card"
          style={{
            padding: 24,
            borderLeft: "4px solid var(--color-success)",
            display: showPostRun ? "block" : "none",
            animation: "fadeInUp 0.5s ease-out"
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 24, marginBottom: 24 }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(74,222,128,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <i className="fa-solid fa-check-circle" style={{ color: "var(--color-success)", fontSize: 32 }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: "var(--text-primary)" }}>Payroll Run Successful!</h3>
              <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 16 }}>
                {monthLabel || selectedMonth} payroll run recorded for {selectedEmployees.length || filteredEmployeeRows.length} selected employee(s).
                {selectedEmployeesTotal != null && selectedEmployeesTotal !== 0
                  ? ` Total disbursement (overall CTC): ${formatInr(selectedEmployeesTotal)}.`
                  : ""}
              </p>
              <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-circle-info" /> Use the detailed `/run-payroll` screen to verify attendance, adjustments, and finalization.
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            <button className="btn-primary" style={{ padding: "12px" }}><i className="fa-solid fa-file-invoice" style={{ marginRight: 8 }}></i>View Payroll Register</button>
            <button className="btn-secondary" style={{ padding: "12px" }}><i className="fa-solid fa-building-columns" style={{ marginRight: 8 }}></i>Generate Bank File</button>
            <button className="btn-secondary" style={{ padding: "12px" }} onClick={handleDownloadSummary}>
              <i className="fa-solid fa-download" style={{ marginRight: 8 }}></i>Download Summary
            </button>
            <button className="btn-ghost" style={{ padding: "12px", border: "1px solid var(--border)" }} onClick={handleSendPayslips}>
              <i className="fa-solid fa-envelope" style={{ marginRight: 8 }}></i>Send Payslips
            </button>
          </div>
        </section>

      </main>

      {/* Add global animation keyframes */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(134, 96, 248, 0.3); }
          50% { box-shadow: 0 0 25px rgba(134, 96, 248, 0.5); }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .glass-card {
          background: var(--pay-surface);
          border: 1px solid var(--pay-border);
          border-radius: 16px;
          transition: all 0.2s ease;
        }
        
        .glass-elevated {
          background: var(--pay-surface-2);
          border: 1px solid var(--pay-border);
          border-radius: 12px;
          transition: all 0.2s ease;
        }
        
        .btn-primary {
          background: var(--primary);
          color: #fff;
          border: none;
          border-radius: 24px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-primary:hover {
          background: var(--primary-hover);
          transform: translateY(-1px);
          box-shadow: 0 6px 25px rgba(134, 96, 248, 0.5);
        }
        
        .btn-secondary {
          background: rgba(169, 163, 201, 0.1);
          color: #fff;
          border: 1px solid rgba(169, 163, 201, 0.2);
          border-radius: 24px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-secondary:hover {
          background: rgba(169, 163, 201, 0.2);
          border-color: rgba(169, 163, 201, 0.3);
        }
        
        .btn-ghost {
          background: transparent;
          color: var(--text-muted);
          border: none;
          border-radius: 24px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
        }
        
        .btn-ghost:hover {
          color: var(--text-primary);
          background: rgba(255, 255, 255, 0.05);
        }

        /* ── Light theme overrides ── */
        .light .glass-card {
          background: #ffffff !important;
          border-color: #e5e7eb !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
        }

        .light .glass-elevated {
          background: #f9fafb !important;
          border-color: #e5e7eb !important;
        }

        .light .btn-secondary {
          color: #1A1A2E !important;
          background: rgba(134,96,248,0.06) !important;
          border-color: rgba(134,96,248,0.2) !important;
        }

        .light .btn-secondary:hover {
          background: rgba(134,96,248,0.12) !important;
        }

        .light .btn-ghost {
          color: #6B6B8A !important;
        }

        .light .btn-ghost:hover {
          background: rgba(134,96,248,0.06) !important;
          color: #1A1A2E !important;
        }

        .light .universal-input {
          background: #ffffff !important;
          border-color: #E0E0F0 !important;
          color: #1A1A2E !important;
        }

        .light .page-btn {
          background: #f9fafb !important;
          color: #6B6B8A !important;
          border-color: #E0E0F0 !important;
        }

        .light .page-btn:hover {
          background: #E9ECF5 !important;
          color: #1A1A2E !important;
        }
        
        .status-chip {
          border-radius: 999px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 28px;
          letter-spacing: 0.01em;
        }

        .status-chip-success {
          background: rgba(74, 222, 128, 0.12);
          color: var(--color-success);
          border: 1px solid rgba(74, 222, 128, 0.2);
        }

        .status-chip-warning {
          background: rgba(245, 158, 11, 0.12);
          color: var(--color-warning);
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .status-chip-error {
          background: rgba(239, 68, 68, 0.12);
          color: var(--color-danger);
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .status-chip-accent {
          background: rgba(134, 96, 248, 0.12);
          color: var(--primary);
          border: 1px solid rgba(134, 96, 248, 0.24);
        }

        .runpay-kpi-card {
          font-family: var(--font, 'Outfit', sans-serif);
          min-height: 170px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .runpay-kpi-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 14px;
        }

        .runpay-kpi-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-muted);
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .runpay-kpi-value {
          font-size: 28px;
          font-weight: 700;
          line-height: 1.05;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .runpay-kpi-sub {
          font-size: 12px;
          color: var(--text-muted);
          line-height: 1.5;
        }

        .runpay-kpi-badge {
          font-size: 10px;
          padding: 5px 10px;
          min-height: 24px;
          line-height: 1.2;
        }

        .employee-cell {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 260px;
        }

        .employee-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          object-fit: cover;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.06);
        }

        .employee-info {
          min-width: 0;
        }

        .employee-name {
          font-weight: 600;
          font-size: 12px;
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .employee-role {
          font-size: 11px;
          color: var(--text-muted);
          margin-top: 2px;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .employee-meta {
          font-size: 11px;
          color: var(--text-muted);
          line-height: 1.4;
        }

        .table-row-hover:hover {
          background: rgba(134, 96, 248, 0.05);
        }
        
        .universal-input {
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: 8px;
          padding: 8px 12px;
          color: var(--input-color);
          font-size: 13px;
          font-family: var(--font);
          outline: none;
          transition: all 0.2s ease;
        }
        
        .universal-input:focus {
          border-color: var(--input-focus-border);
          box-shadow: 0 0 0 3px var(--input-focus-shadow);
        }
        
        .w-100 {
          width: 100%;
        }
        
        .page-btn {
          transition: all 0.2s ease;
        }
        
        .page-btn:hover {
          background: var(--border);
          color: var(--text-primary);
        }
        
        .page-btn.active {
          background: var(--primary) !important;
          color: #fff !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};

export default RunPayroll;
